'use client';

import { useMemo, useState } from 'react';
import { GroupByOption, PaginatedResponse, WriteOffStatus } from '@repo/types';
import { Card } from '@repo/ui';
import { useUpdateWriteOff } from '@xtnd/web/features/ar-collections/queries/writeOffsQuery';
import { DataTable } from '@xtnd/web/shared/components/DataTable';
import { QueryStringParams } from '@xtnd/web/shared';

import { WRITE_OFF_TABLE_DEFAULT_PROPS } from '../../constants';
import {
  DIALOG_MODE_MAP,
  UseWriteOffTabReturn,
  useWriteOffTab,
  WriteOffTabProps,
} from '../../hooks';
import { WriteOff, ArWriteOffFilter, WriteOffItem, FlatWriteOffItem } from '../../types/writeOffs';
import ActionDialog from './ActionDialog';
import ClientInvoicesTable from './ClientInvoicesTable';
import { WriteOffToolbar } from './FilterToolbars';
import { WriteOffTabHeader } from './WriteOffTabHeader';
import { WriteOffActivity } from './WriteOffActivity';

const config = {
  title: 'All Write-Offs',
  description: '',
  emptyState: {
    icon: 'HiClipboardList',
    title: 'No write-offs',
    description: 'There are no write-offs found.',
  },
  // Combined dialog IDs for all possible actions
  dialogIds: ['approve', 'escalated', 'archive', 'reject', 'note'],
  // Combined actions with status-based filtering
  actions: [
    // For PENDING and ESCALATED items
    {
      icon: 'HiCheckCircle',
      iconColor: 'text-green-600',
      label: 'Approve',
      dialogId: 'approve',
      shouldShow: (row: any) =>
        row.writeOffStatus === WriteOffStatus.PENDING ||
        row.writeOffStatus === WriteOffStatus.ESCALATED,
    },
    // For PENDING items only
    {
      icon: 'HiXCircle',
      iconColor: 'text-red-600',
      label: 'Request Review',
      dialogId: 'escalated',
      shouldShow: (row: any) => row.writeOffStatus === WriteOffStatus.PENDING,
    },
    // For ESCALATED items only
    {
      icon: 'HiXCircle',
      iconColor: 'text-red-600',
      label: 'Reject & Restart Appeal',
      dialogId: 'reject',
      shouldShow: (row: any) => row.writeOffStatus === WriteOffStatus.ESCALATED,
    },
    // For APPROVED items only
    {
      icon: 'HiArchive',
      iconColor: 'text-blue-600',
      label: 'Mark Processed',
      dialogId: 'archive',
      shouldShow: (row: any) => row.writeOffStatus === WriteOffStatus.APPROVED,
    },
    // For ALL items
    {
      icon: 'HiChat',
      iconColor: 'text-xtnd-blue',
      label: 'Comment',
      dialogId: 'note',
      showDividerBefore: true,
    },
  ],
  supportsLocalStateUpdate: true,
};

// ============================================================================
// Main Component
// ============================================================================

export default function AllTab({
  isAdmin,
  tableParams,
  setTableParams,
  data,
  isLoading,
  error,
  refetch,
}: WriteOffTabProps) {
  // Activity drawer state
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivityRow, setSelectedActivityRow] = useState<WriteOff | null>(null);

  const handleOpenActivity = (row: WriteOff) => {
    setSelectedActivityRow(row);
    setIsActivityOpen(true);
  };

  const tab = useWriteOffTab({
    tabType: 'all',
    isAdmin,
    tableParams,
    setTableParams,
    dialogIds: [...config.dialogIds],
    actions: [...config.actions],
    supportsLocalStateUpdate: config.supportsLocalStateUpdate,
    onOpenActivity: handleOpenActivity,
  });

  const { mutateAsync: patchWriteOff } = useUpdateWriteOff(
    tableParams,
    isAdmin,
    tab.handleLocalStateUpdate,
    tab.handleLocalCommentUpdate
  );

  const combinedLoading = isLoading;

  const filters = (tableParams.filters as ArWriteOffFilter) ?? {};
  const isDisplayInvoicesTable = !!filters.entityId && filters.groupBy === GroupByOption.CLIENT;

  return (
    <Card className="pt-0 mt-0">
      <div className="p-0 relative">
        {isDisplayInvoicesTable ? (
          <div className="py-4">
            <ClientInvoicesTable
              invoices={data?.data || []}
              clientName={tab.clientName}
              columns={tab.columns[GroupByOption.NONE]}
              onBackToClient={() => {
                // Remove entityId from filters
                const newFilters = { ...filters };
                delete newFilters.entityId;
                setTableParams({ ...tableParams, filters: newFilters, page: 1 });
                tab.resetInvoices();
              }}
              isDataTableLoading={isLoading}
              filters={tab.invoiceFilters}
              onFiltersChange={tab.setInvoiceFilters}
              getRowClassName={tab.getRowClassName}
              shouldFlatten
            />
          </div>
        ) : (
          <>
            {/* Header Component */}
            <AllTabHeader tab={tab} isLoading={combinedLoading} onRefresh={refetch} />

            {/* Data Table Component */}
            <AllTabTable
              tab={tab}
              data={data}
              tableParams={tableParams}
              setTableParams={setTableParams}
              isLoading={combinedLoading}
              error={error}
              onRetry={refetch}
            />
          </>
        )}

        {/* Action Dialogs - rendered at top level so they work in both views */}
        {config.dialogIds.map(id => {
          const dialog = tab.dialogs[id];
          return (
            <ActionDialog
              key={id}
              isOpen={dialog?.isOpen ?? false}
              onClose={dialog?.close ?? (() => {})}
              writeOff={dialog?.selectedWriteOff ?? null}
              mode={DIALOG_MODE_MAP[id] ?? 'note'}
              onMutationStateChange={tab.handleMutationStateChange}
              onSubmit={patchWriteOff}
            />
          );
        })}

        {/* Activity Drawer */}
        <WriteOffActivity
          isOpen={isActivityOpen}
          onClose={() => {
            setIsActivityOpen(false);
            setSelectedActivityRow(null);
          }}
          invoiceNumber={selectedActivityRow?.invoiceNumber ?? ''}
          invoiceId={selectedActivityRow?.invoiceId ?? ''}
          tabType={tab.tabType}
        />
      </div>
    </Card>
  );
}

// ============================================================================
// Header Component
// ============================================================================

interface AllTabHeaderProps {
  tab: UseWriteOffTabReturn;
  isLoading: boolean;
  onRefresh: () => void;
}

function AllTabHeader({ tab, isLoading, onRefresh }: Readonly<AllTabHeaderProps>) {
  return (
    <WriteOffTabHeader
      title={config.title}
      description={config.description}
      isLoading={isLoading}
      onRefresh={onRefresh}
      renderToolbar={() => (
        <WriteOffToolbar
          isDataTableLoading={isLoading}
          filters={tab.filters}
          onChange={tab.handleChangeFilters}
        />
      )}
    />
  );
}

// ============================================================================
// Data Table Component
// =================================0===========================================

interface AllTabTableProps {
  tab: UseWriteOffTabReturn;
  data?: PaginatedResponse<WriteOff>;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  tableParams: QueryStringParams;
  setTableParams: (params: Partial<QueryStringParams>) => void;
}

function AllTabTable({
  tab,
  data,
  isLoading,
  error,
  onRetry,
  tableParams,
  setTableParams,
}: Readonly<AllTabTableProps>) {
  // Flatten data for table display
  const flatData = useMemo(() => {
    if (!data?.data) return [];
    if (tab.filters.groupBy === GroupByOption.CLIENT) return data.data;

    return data.data.flatMap(invoice => {
      const items = invoice.writeOffs;

      if (items?.length) {
        return items.map((item: WriteOffItem) => ({
          ...invoice,
          ...item,
          writeOffStatus: item.status,
          invoice: invoice,
          _parentInvoice: invoice,
          writeOff: item,
          writeOffAmount: item.amount,
        })) as unknown as FlatWriteOffItem;
      }

      // Fallback
      const statuses = Array.isArray(invoice.writeOffStatus)
        ? invoice.writeOffStatus
        : [invoice.writeOffStatus];

      return statuses.map((status: any) => ({
        ...invoice,
        writeOffStatus: status,
        invoice: invoice,
        category: undefined,
        type: undefined,
        amount: invoice.writeOffAmount,
      })) as unknown as FlatWriteOffItem;
    });
  }, [data, tab.filters.groupBy]);

  return (
    <>
      {/* Data Table */}
      <div className="mt-4">
        <DataTable
          {...WRITE_OFF_TABLE_DEFAULT_PROPS}
          columns={tab.columns[tab.filters.groupBy] ?? []}
          data={{
            data: flatData,
            total: data?.pagination?.total ?? 0,
          }}
          isLoading={isLoading}
          error={error}
          onRetry={onRetry}
          onSort={sortBy => setTableParams({ ...tableParams, sorts: { ...sortBy } })}
          sorting={tableParams.sorts}
          pagination={data?.pagination}
          onPaginationChange={tab.handlePaginationChange}
          emptyState={config.emptyState}
          onRowClick={tab.handleRowClicked as any}
          getRowClassName={tab.getRowClassName as any}
          enableColumnResizing
          columnWidthsStorageKey={`writeoffs-all-table-${tab.filters.groupBy}`}
        />
      </div>
    </>
  );
}
